import type {
  AgeLimitRestrictionT,
  CoverageMessageT,
  CoverageResourceT,
  CoverageRestrictionsT,
  FormularyResponseT,
  PatientSpecificPricingCoverageT,
  PayerAlternativeT,
  PayerCopayT,
  PayerInformationT,
  PrescriptionBenefitPayerAlternativeDetailT,
  PrescriptionBenefitsPharmacyT,
  PrescriptionBenefitsResponseT,
  PricingCoverageFactorsT,
  QuantityLimitRestrictionT,
  StepMedicationRestrictionT,
  TherapeuticAlternativesT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import {
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  CreditCard,
  DollarSign,
  Eye,
  ExternalLink,
  FileText,
  Info,
  ListTree,
  Loader2,
  MessageSquare,
  Pill,
  ShieldCheck,
  Store,
} from 'lucide-react';
import { useState } from 'react';

import type { RxMedication } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/shadcn/dialog';
import { cn } from '@/lib/utils';

import { FormularyDetailDialog } from './FormularyDetailDialog';

import {
  type FormularyDisplayState,
  bucketFormularyStatus,
  describeCopay,
  deriveFormularyDisplayState,
  formularyStatusLabel,
  pharmacyTypeLabel,
  pickAlternativeRetailCopay,
} from '../model/formularyDisplayPolicy';

export interface BenefitsCoordinationPanelProps {
  payerInformation: PayerInformationT | null | undefined;
  formulary: FormularyResponseT | null | undefined;
  benefits: PrescriptionBenefitsResponseT | null | undefined;
  /** The currently-selected medication, used for multi-ingredient + obsolete policy. */
  medication: RxMedication | null | undefined;
  /**
   * Optional callback invoked when the clinician picks a payer-preferred
   * or therapeutic alternative. The integer is a DoseSpot
   * ``DispensableDrugId`` ready to be passed to ``select_medication``.
   * When omitted, alternatives render as read-only.
   *
   * May return a promise — callers can await the underlying swap before
   * dismissing, e.g. the Details-step modal closes after the swap
   * completes. The return value is ignored either way.
   */
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  /** Disable the alternative buttons, e.g. while a switch is in flight. */
  isSelectingAlternative?: boolean;
  /** True once the caller knows a coverage lookup completed, even if it returned no payloads. */
  hasCoverageResponse?: boolean;
  /** True only when the benefits payload is from the current query key, not placeholder data. */
  hasConfirmedBenefitsResponse?: boolean;
  /** When true, shows a loading indicator over the RTPB section (e.g. eligibility switch). */
  isLoadingBenefits?: boolean;
}

/**
 * Displays BenefitsCoordination data per ACR PA.109.1 and the F1–F7
 * cert matrix — payer coverage details, formulary status, drug-level
 * restrictions, copays, and alternatives.
 *
 * Sections (rendered top-to-bottom in order of decision relevance):
 *
 * 1. Payer (271 response): which plan / member id we will be billing.
 * 2. Formulary status + flags + multi-channel copays + restriction
 *    details (step meds, qty limits, age/gender, messages, links).
 * 3. Payer-preferred alternatives — sorted by preference level.
 * 4. Therapeutic alternatives — only multi-ingredient when target is.
 * 5. RTPB pricing: real-time benefits cost data when present.
 *
 * Display policy (Unknown/Obsolete suppression, payer-vs-therapeutic
 * mutex, multi-ingredient filter) is centralised in
 * :mod:`formularyDisplayPolicy`.
 */
export function BenefitsCoordinationPanel({
  payerInformation,
  formulary,
  benefits,
  medication,
  onSelectAlternative,
  isSelectingAlternative = false,
  hasCoverageResponse = false,
  hasConfirmedBenefitsResponse = false,
  isLoadingBenefits = false,
}: BenefitsCoordinationPanelProps) {
  const [alternativesOpen, setAlternativesOpen] = useState(false);
  const [rtpbOpen, setRtpbOpen] = useState(false);

  const hasAnyData = Boolean(payerInformation || formulary || benefits);
  if (!hasAnyData) {
    if (!hasCoverageResponse) return null;

    return (
      <div
        className={cn(
          'rounded-lg border border-border/60 bg-muted/30 p-4',
          'dark:border-border/40 dark:bg-muted/20'
        )}
      >
        <PanelHeader />
        <EmptyCoverageState />
      </div>
    );
  }

  const display = deriveFormularyDisplayState({ formulary, medication });
  const formularyStatus = formulary?.FormularyStatus ?? null;
  const restrictions = formulary?.Restrictions ?? null;
  const copays = formulary?.Copays ?? null;
  const hasRtpbDetailsPayload = hasRtpbDetails(benefits);
  const alternativeCount =
    display.payerAlternatives.length + display.therapeuticAlternatives.length;

  return (
    <div
      className={cn(
        'rounded-lg border border-border/60 bg-muted/30 p-4',
        'dark:border-border/40 dark:bg-muted/20'
      )}
    >
      <PanelHeader />

      <div className="space-y-4">
        {payerInformation ? <PayerSection payer={payerInformation} /> : null}

        {formulary ? (
          <FormularySection
            formularyStatus={formularyStatus}
            realtimeStatus={formulary.RealTimeFormularyRequestStatus}
            restrictions={restrictions}
            copays={copays}
            display={display}
            payerInformation={payerInformation}
          />
        ) : null}

        {display.policy.notice ? <NoticeRow text={display.policy.notice} /> : null}

        {alternativeCount > 0 ? (
          <>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setAlternativesOpen(true)}
            >
              <Pill className="size-3.5" aria-hidden />
              View Alternatives ({alternativeCount})
            </Button>
            <PanelAlternativesDialog
              open={alternativesOpen}
              onOpenChange={setAlternativesOpen}
              display={display}
              payerInformation={payerInformation}
              onSelectAlternative={onSelectAlternative}
              isSelectingAlternative={isSelectingAlternative}
            />
          </>
        ) : null}

        {isLoadingBenefits && !hasRtpbDetailsPayload ? (
          <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Loading real-time benefits…
          </div>
        ) : null}
        {hasRtpbDetailsPayload ? (
          <div className="relative">
            {isLoadingBenefits ? (
              <div className="absolute -inset-2 z-10 flex items-center justify-center rounded-lg bg-background/60">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  Updating benefits…
                </div>
              </div>
            ) : null}
            <RtpbInlineSummary benefits={benefits!} onViewDetails={() => setRtpbOpen(true)} />
          </div>
        ) : benefits && hasConfirmedBenefitsResponse ? (
          <RtpbEmptyState realtimeStatus={benefits.RealTimeFormularyRequestStatus ?? null} />
        ) : null}
        {hasRtpbDetailsPayload ? (
          <RtpbDialog
            open={rtpbOpen}
            onOpenChange={setRtpbOpen}
            benefits={benefits!}
            onSelectAlternative={onSelectAlternative}
            isSelectingAlternative={isSelectingAlternative}
          />
        ) : null}
      </div>
    </div>
  );
}

function PanelHeader() {
  return (
    <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
      <CreditCard className="size-3.5" aria-hidden />
      Benefits Coordination
    </div>
  );
}

function EmptyCoverageState() {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs dark:border-border/30 dark:bg-background/20">
      <div className="font-medium text-foreground/90">No coverage data returned</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        The coverage lookup completed, but no payer, formulary, or RTPB details were returned.
      </div>
    </div>
  );
}

function PanelAlternativesDialog({
  open,
  onOpenChange,
  display,
  payerInformation,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  display: FormularyDisplayState;
  payerInformation: PayerInformationT | null | undefined;
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  const planLabel = payerInformation?.PlanName ?? payerInformation?.PayerName ?? null;

  const handleSelect = onSelectAlternative
    ? async (dispensableDrugId: number) => {
        await onSelectAlternative(dispensableDrugId);
        onOpenChange(false);
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pill className="size-4" aria-hidden />
            Payer Alternatives
            {planLabel ? (
              <span className="text-sm font-normal text-muted-foreground">— {planLabel}</span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {display.policy.notice ? (
            <div className="rounded-md border border-border/40 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
              {display.policy.notice}
            </div>
          ) : null}

          {display.payerAlternatives.length > 0 ? (
            <PayerAlternativesSection
              alternatives={display.payerAlternatives}
              onSelectAlternative={handleSelect}
              isSelectingAlternative={isSelectingAlternative}
            />
          ) : null}

          {display.therapeuticAlternatives.length > 0 ? (
            <TherapeuticAlternativesSection
              alternatives={display.therapeuticAlternatives}
              onSelectAlternative={handleSelect}
              isSelectingAlternative={isSelectingAlternative}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RtpbInlineSummary({
  benefits,
  onViewDetails,
}: {
  benefits: PrescriptionBenefitsResponseT;
  onViewDetails: () => void;
}) {
  const coverages = benefits.PatientSpecificPricingCoverages ?? [];
  const { primary, others } = partitionCoveragesByPharmacyType(coverages);
  const pricing = primary?.PricingCoverage;
  const isDrugDiscount = isDrugDiscountResponse(coverages);
  const hasAlerts = (pricing?.CoverageAlerts?.length ?? 0) > 0;
  const alternativeCount = normalizeRtpbAlternatives(benefits.Alternatives ?? []).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <DollarSign className="size-3" aria-hidden />
        {isDrugDiscount ? 'Drug Discount Pricing' : 'Real-Time Benefits (RTPB)'}
      </div>

      {pricing ? (
        <div className="rounded-md border border-border/50 bg-background/40 p-2.5 dark:border-border/30 dark:bg-background/20">
          <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
            <PricingField
              label="Est. Patient Pay"
              value={pricing.EstimatedPatientPayAmount}
              prefix="$"
            />
            <PricingField label="Qty Priced" value={formatQuantityPriced(pricing)} />
            <PricingField label="Days Supply" value={pricing.DaysSupplyPriced} />
          </div>
        </div>
      ) : null}

      <Button variant="outline" size="sm" className="gap-1.5" onClick={onViewDetails}>
        <DollarSign className="size-3.5" aria-hidden />
        Full RTPB Details
        {pricing?.PriorAuthRequired ? (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            PA
          </span>
        ) : null}
        {hasAlerts ? (
          <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
            Alerts
          </span>
        ) : null}
        {others.length > 0 ? (
          <span className="text-[10px] text-muted-foreground">
            +{others.length} {others.length === 1 ? 'pharmacy' : 'pharmacies'}
          </span>
        ) : null}
        {alternativeCount > 0 ? (
          <span className="text-[10px] text-muted-foreground">
            {alternativeCount} {alternativeCount === 1 ? 'alt' : 'alts'}
          </span>
        ) : null}
      </Button>
    </div>
  );
}

function RtpbDialog({
  open,
  onOpenChange,
  benefits,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  benefits: PrescriptionBenefitsResponseT;
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="size-4" aria-hidden />
            Real-Time Benefits (RTPB)
          </DialogTitle>
        </DialogHeader>
        <RtpbSection
          benefits={benefits}
          onSelectAlternative={onSelectAlternative}
          isSelectingAlternative={isSelectingAlternative}
        />
      </DialogContent>
    </Dialog>
  );
}

function PayerSection({ payer }: { payer: PayerInformationT }) {
  const fields = [
    { label: 'Eligibility ID', value: payer.PatientEligibilityId },
    { label: 'Payer', value: payer.PayerName },
    { label: 'Payer ID', value: payer.PayerId },
    { label: 'Plan', value: payer.PlanName },
    { label: 'BIN', value: payer.BIN },
    { label: 'PCN', value: payer.PCN },
    { label: 'Member ID', value: payer.MemberId },
    { label: 'Coverage ID', value: payer.CoverageId },
    { label: 'Formulary ID', value: payer.FormularyId },
    { label: 'Alternatives ID', value: payer.AlternativesId },
    { label: 'Copay ID', value: payer.CopayId },
  ].filter((f) => f.value != null && f.value !== '');
  const messages = [
    { label: 'Payer error', value: payer.ErrorMessage },
    { label: 'Payer note', value: payer.InfoMessage },
  ].filter((f) => f.value != null && f.value !== '');

  if (fields.length === 0 && messages.length === 0) return null;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <ShieldCheck className="size-3" aria-hidden />
        Coverage (271 Response)
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 @md:grid-cols-3">
        {fields.map(({ label, value }) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="break-words text-xs font-medium">{value}</div>
          </div>
        ))}
      </div>
      {messages.length > 0 ? (
        <div className="mt-2 space-y-1">
          {messages.map(({ label, value }) => (
            <div
              key={label}
              className="rounded-md border border-border/40 bg-background/40 px-2 py-1 text-xs dark:border-border/30 dark:bg-background/20"
            >
              <span className="font-medium">{label}: </span>
              <span className="text-muted-foreground">{value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FormularySection({
  formularyStatus,
  realtimeStatus,
  restrictions,
  copays,
  display,
  payerInformation,
}: {
  formularyStatus: string | null | undefined;
  realtimeStatus: number | null | undefined;
  restrictions: CoverageRestrictionsT | null | undefined;
  copays: PayerCopayT[] | null | undefined;
  display: FormularyDisplayState;
  payerInformation: PayerInformationT | null | undefined;
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const statusLabel = displayStatusLabel(formularyStatus, display.bucket);

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Info className="size-3" aria-hidden />
        Formulary
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-5 gap-1 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => setDetailOpen(true)}
        >
          <Eye className="size-3" aria-hidden />
          Full details
        </Button>
      </div>
      <div className="space-y-2.5">
        {statusLabel ? (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</div>
            <div className="text-xs font-medium">{statusLabel}</div>
          </div>
        ) : null}

        {realtimeStatus != null ? (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Realtime status
            </div>
            <div className="text-xs font-medium">{describeRealtimeStatus(realtimeStatus)}</div>
          </div>
        ) : null}

        {!display.hideCopay && copays && copays.length > 0 ? (
          <CopaysSubsection copays={copays} />
        ) : null}

        {restrictions ? <RestrictionsSubsection restrictions={restrictions} /> : null}
      </div>

      <FormularyDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        restrictions={restrictions}
        copays={copays}
        payerInformation={payerInformation}
      />
    </div>
  );
}

function displayStatusLabel(
  formularyStatus: string | null | undefined,
  bucket: FormularyDisplayState['bucket']
): string | null {
  const explicit = formularyStatusLabel(formularyStatus) ?? formularyStatus;
  if (explicit != null && explicit.trim() !== '') return explicit;
  switch (bucket) {
    case 'on-formulary-preferred':
      return 'On Formulary, Preferred';
    case 'on-formulary-not-preferred':
      return 'On Formulary, Not Preferred';
    case 'non-formulary':
      return 'Non-Formulary';
    case 'not-reimbursable':
      return 'Not Reimbursable';
    case 'excluded':
      return 'Excluded';
    case 'unknown':
      return 'Unknown';
  }
}

function describeRealtimeStatus(status: number): string {
  switch (status) {
    case 0:
      return 'Queued';
    case 1:
      return 'Sent';
    case 2:
      return 'Success';
    case 3:
      return 'No realtime lookup';
    default:
      return 'Error or unavailable';
  }
}

function CopaysSubsection({ copays }: { copays: PayerCopayT[] }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Copay</div>
      <div className="flex flex-col gap-1">
        {copays.map((copay, idx) => {
          const description = describeCopay(copay);
          if (description == null) return null;
          return (
            <div
              key={`copay-${idx}`}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-md border border-border/40 bg-background/40 px-2 py-1 text-xs dark:border-border/30 dark:bg-background/20"
            >
              <span className="font-medium">{pharmacyTypeLabel(copay.PharmacyType ?? null)}</span>
              <span className="text-muted-foreground">·</span>
              <span>{description}</span>
              {copay.IsDrugSpecific === false ? (
                <span className="ml-auto text-[10px] text-muted-foreground">summary</span>
              ) : null}
              {(copay.OutOfPocketMinimum != null || copay.OutOfPocketMaximum != null) && (
                <span className="ml-auto text-[10px] text-muted-foreground">
                  OOP {copay.OutOfPocketMinimum != null ? `$${copay.OutOfPocketMinimum}` : '—'}
                  {' / '}
                  {copay.OutOfPocketMaximum != null ? `$${copay.OutOfPocketMaximum}` : '—'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RestrictionsSubsection({ restrictions }: { restrictions: CoverageRestrictionsT }) {
  const flags: string[] = [];
  if (restrictions.HasPriorAuthorization) flags.push('Prior Auth');
  if (restrictions.HasStepTherapy) flags.push('Step Therapy');
  if (restrictions.HasQuantityLimit) flags.push('Qty Limit');
  if (restrictions.HasAgeLimit) flags.push('Age Limit');
  if (restrictions.HasMedicalNecessity) flags.push('Medical Necessity');
  if (restrictions.IsExcluded) flags.push('Excluded');
  if (restrictions.GenderRestriction != null && restrictions.GenderRestriction !== 'Unknown') {
    flags.push(`${restrictions.GenderRestriction} only`);
  }

  const stepMeds = restrictions.StepMedicationRestrictions ?? [];
  const quantityLimits = restrictions.QuantityLimitRestrictions ?? [];
  const ageLimits = restrictions.AgeLimitRestrictions ?? [];
  const messages = restrictions.Messages ?? [];
  const drugResources = restrictions.DrugResources ?? [];
  const summaryResources = restrictions.SummaryResources ?? [];

  const hasAnyDetail =
    flags.length > 0 ||
    stepMeds.length > 0 ||
    quantityLimits.length > 0 ||
    ageLimits.length > 0 ||
    messages.length > 0 ||
    drugResources.length > 0 ||
    summaryResources.length > 0;

  if (!hasAnyDetail) return null;

  return (
    <div className="space-y-2">
      {flags.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Restrictions
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {flags.map((flag) => (
              <span
                key={flag}
                className="rounded-full border border-amber-400/50 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
              >
                {flag}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {stepMeds.length > 0 ? <StepMedicationsBlock stepMeds={stepMeds} /> : null}
      {quantityLimits.length > 0 ? <QuantityLimitsBlock limits={quantityLimits} /> : null}
      {ageLimits.length > 0 ? <AgeLimitsBlock limits={ageLimits} /> : null}
      {messages.length > 0 ? <MessagesBlock messages={messages} /> : null}
      {drugResources.length > 0 ? (
        <ResourcesBlock label="Drug-specific resources" resources={drugResources} />
      ) : null}
      {summaryResources.length > 0 ? (
        <ResourcesBlock label="Plan resources" resources={summaryResources} />
      ) : null}
    </div>
  );
}

function StepMedicationsBlock({ stepMeds }: { stepMeds: StepMedicationRestrictionT[] }) {
  const sorted = [...stepMeds].sort((a, b) => (a.StepOrder ?? 0) - (b.StepOrder ?? 0));
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <ListTree className="size-3" aria-hidden />
        Step Therapy Sequence
      </div>
      <ol className="mt-1 list-decimal space-y-0.5 pl-4">
        {sorted.map((step, idx) => (
          <li key={step.Ndc ?? `step-${idx}`} className="text-xs">
            {step.DisplayName ?? step.Ndc ?? '—'}
          </li>
        ))}
      </ol>
    </div>
  );
}

function QuantityLimitsBlock({ limits }: { limits: QuantityLimitRestrictionT[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        Quantity Limits
      </div>
      <ul className="mt-1 space-y-0.5 text-xs">
        {limits.map((limit, idx) => (
          <li key={`qty-${idx}`}>· {describeQuantityLimit(limit)}</li>
        ))}
      </ul>
    </div>
  );
}

function describeQuantityLimit(limit: QuantityLimitRestrictionT): string {
  const amount = limit.MaximumAmount != null ? `${limit.MaximumAmount}` : '—';
  const amountUnit = limit.AmountUnit ?? '';
  const periodUnits = limit.MaximumTimePeriodUnits;
  const periodKind = limit.TimePeriodUnit ?? '';

  let phrase = `${amount} ${humanizeAmountUnit(amountUnit)}`.trim();
  if (periodUnits != null && periodKind !== '' && periodKind !== 'Lifetime') {
    phrase += ` per ${periodUnits} ${humanizePeriodUnit(periodKind)}`;
  } else if (periodKind === 'Lifetime') {
    phrase += ' for life';
  } else if (periodKind === 'PerDispensing') {
    phrase += ' per dispensing';
  } else if (periodKind === 'SpecificDateRange' && limit.StartDate && limit.EndDate) {
    phrase += ` between ${limit.StartDate} and ${limit.EndDate}`;
  }
  return phrase;
}

function humanizeAmountUnit(unit: string): string {
  switch (unit) {
    case 'Quantity':
      return 'units';
    case 'DaysSupply':
      return 'days supply';
    case 'Fills':
      return 'fills';
    case 'DollarAmount':
      return 'dollars';
    default:
      return unit;
  }
}

function humanizePeriodUnit(unit: string): string {
  switch (unit) {
    case 'Days':
      return 'days';
    case 'CalendarYear':
      return 'calendar year';
    case 'CalendarQuarter':
      return 'calendar quarter';
    case 'CalendarMonth':
      return 'calendar month';
    case 'PerDispensing':
      return 'dispensing';
    case 'SpecificDateRange':
      return 'date range';
    default:
      return unit;
  }
}

function AgeLimitsBlock({ limits }: { limits: AgeLimitRestrictionT[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Age Limits</div>
      <ul className="mt-1 space-y-0.5 text-xs">
        {limits.map((limit, idx) => (
          <li key={`age-${idx}`}>· {describeAgeLimit(limit)}</li>
        ))}
      </ul>
    </div>
  );
}

function describeAgeLimit(limit: AgeLimitRestrictionT): string {
  const parts: string[] = [];
  if (limit.MinimumAge != null) {
    parts.push(`min ${limit.MinimumAge} ${limit.MinimumAgeUnit?.toLowerCase() ?? 'years'}`);
  }
  if (limit.MaximumAge != null) {
    parts.push(`max ${limit.MaximumAge} ${limit.MaximumAgeUnit?.toLowerCase() ?? 'years'}`);
  }
  return parts.length > 0 ? parts.join(' / ') : 'Age limit applies';
}

function MessagesBlock({ messages }: { messages: CoverageMessageT[] }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <MessageSquare className="size-3" aria-hidden />
        Coverage Messages
      </div>
      <ul className="mt-1 space-y-1">
        {messages.map((msg, idx) => {
          const text = msg.LongMessage ?? msg.Message ?? null;
          if (text == null || text.trim() === '') return null;
          return (
            <li
              key={`msg-${idx}`}
              className="rounded-md border border-border/40 bg-background/40 px-2 py-1 text-xs dark:border-border/30 dark:bg-background/20"
            >
              {text}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ResourcesBlock({ label, resources }: { label: string; resources: CoverageResourceT[] }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <FileText className="size-3" aria-hidden />
        {label}
      </div>
      <ul className="mt-1 space-y-0.5">
        {resources.map((resource, idx) => {
          if (resource.Url == null || resource.Url.trim() === '') return null;
          return (
            <li key={`resource-${idx}`} className="text-xs">
              <a
                href={resource.Url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-sky-700 underline-offset-2 hover:underline dark:text-sky-300"
              >
                {resource.ResourceType ?? 'Resource'}
                <ExternalLink className="size-3" aria-hidden />
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NoticeRow({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-[11px] text-muted-foreground dark:border-border/30 dark:bg-background/20">
      {text}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  RTPB — Real-Time Prescription Benefits (PM.105–PM.112)            */
/* ------------------------------------------------------------------ */

function hasRtpbData(benefits: PrescriptionBenefitsResponseT | null | undefined): boolean {
  const coverages = benefits?.PatientSpecificPricingCoverages;
  return (coverages?.length ?? 0) > 0;
}

function hasRtpbDetails(benefits: PrescriptionBenefitsResponseT | null | undefined): boolean {
  if (!benefits) return false;
  return (
    hasRtpbData(benefits) ||
    normalizeRtpbAlternatives(benefits.Alternatives ?? []).length > 0 ||
    benefits.OptionalPharmacy != null
  );
}

function RtpbEmptyState({ realtimeStatus }: { realtimeStatus: number | null }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs dark:border-border/30 dark:bg-background/20">
      <div className="font-medium text-foreground/90">No RTPB pricing returned</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        DoseSpot returned a benefits payload, but no patient-specific pricing coverage rows.
        {realtimeStatus != null
          ? ` Realtime status: ${describeRealtimeStatus(realtimeStatus)}.`
          : ''}
      </div>
    </div>
  );
}

function isDrugDiscountResponse(coverages: PatientSpecificPricingCoverageT[]): boolean {
  return coverages.some((c) => {
    const status = c.PricingCoverage?.DrugStatus ?? '';
    return status.toLowerCase().includes('drug discount price');
  });
}

function RtpbSection({
  benefits,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  benefits: PrescriptionBenefitsResponseT;
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  const coverages = benefits.PatientSpecificPricingCoverages ?? [];
  const alternatives = benefits.Alternatives ?? [];
  const [showOtherPharmacies, setShowOtherPharmacies] = useState(false);

  const { primary, others } = partitionCoveragesByPharmacyType(coverages);
  const isDrugDiscount = isDrugDiscountResponse(coverages);

  return (
    <div className="space-y-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <DollarSign className="size-3" aria-hidden />
        {isDrugDiscount ? 'Drug Discount Pricing' : 'Real-Time Benefits (RTPB)'}
      </div>

      {isDrugDiscount ? (
        <div className="rounded-md border border-violet-400/40 bg-violet-50/60 px-2.5 py-1.5 text-[11px] font-medium text-violet-800 dark:border-violet-500/30 dark:bg-violet-950/30 dark:text-violet-200">
          This is Drug Discount Pricing — the patient has no active PBM coverage for this
          medication. Pricing shown is a discount offer, not an insurance benefit.
        </div>
      ) : null}

      <div className="rounded-md border border-amber-400/30 bg-amber-50/50 px-2.5 py-1.5 text-[10px] text-amber-800 dark:border-amber-500/20 dark:bg-amber-950/30 dark:text-amber-200">
        <AlertTriangle className="mr-1 inline-block size-3 align-text-bottom" aria-hidden />
        Pricing is a point-in-time estimate based on the quantity and days supply provided. Actual
        cost may vary at the dispensing pharmacy. Alternative information is informational only and
        not intended to replace clinical decisions.
      </div>

      {benefits.OptionalPharmacy && !primary?.Pharmacy ? (
        <PharmacyDetailBlock pharmacy={benefits.OptionalPharmacy} />
      ) : null}

      {coverages.length === 0 ? (
        <RtpbEmptyState realtimeStatus={benefits.RealTimeFormularyRequestStatus ?? null} />
      ) : null}

      {primary ? (
        <PricingCoverageCard coverage={primary} label="Requested pharmacy" defaultExpanded />
      ) : null}

      {others.length > 0 ? (
        <div>
          <button
            type="button"
            className="flex w-full items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setShowOtherPharmacies((v) => !v)}
          >
            <Store className="size-3" aria-hidden />
            Pricing at {others.length} other {others.length === 1 ? 'pharmacy' : 'pharmacies'}
            <ChevronDown
              className={cn(
                'ml-auto size-3.5 transition-transform',
                showOtherPharmacies && 'rotate-180'
              )}
              aria-hidden
            />
          </button>
          {showOtherPharmacies ? (
            <div className="mt-2 space-y-2">
              {others.map((cov, idx) => (
                <PricingCoverageCard
                  key={`other-pharm-${idx}`}
                  coverage={cov}
                  label={pharmacyLabel(cov)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {alternatives.length > 0 ? (
        <RtpbAlternativesSection
          alternatives={alternatives}
          onSelectAlternative={onSelectAlternative}
          isSelectingAlternative={isSelectingAlternative}
        />
      ) : null}
    </div>
  );
}

function partitionCoveragesByPharmacyType(coverages: PatientSpecificPricingCoverageT[]): {
  primary: PatientSpecificPricingCoverageT | null;
  others: PatientSpecificPricingCoverageT[];
} {
  const retailIdx = coverages.findIndex(
    (c) => c.Pharmacy?.PharmacyType?.toLowerCase() === 'retail'
  );
  const primaryIdx = retailIdx >= 0 ? retailIdx : 0;
  const primary = coverages[primaryIdx] ?? null;
  const others = coverages.filter((_, idx) => idx !== primaryIdx);
  return { primary, others };
}

function pharmacyLabel(coverage: PatientSpecificPricingCoverageT): string {
  const type = coverage.Pharmacy?.PharmacyType;
  const name = coverage.Pharmacy?.StoreName;
  if (type && name) return `${type} — ${name}`;
  return type ?? name ?? 'Pharmacy';
}

function formatPharmacyAddress(pharmacy: PrescriptionBenefitsPharmacyT): string | null {
  const parts = [pharmacy.Address1, pharmacy.Address2].filter(Boolean);
  const cityState = [pharmacy.City, pharmacy.State].filter(Boolean).join(', ');
  const cityStateZip = [cityState, pharmacy.ZipCode].filter(Boolean).join(' ');
  if (cityStateZip) parts.push(cityStateZip);
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatPharmacistName(pharmacy: PrescriptionBenefitsPharmacyT): string | null {
  const parts = [
    pharmacy.PharmacistPrefix,
    pharmacy.PharmacistFirstName,
    pharmacy.PharmacistMiddleName,
    pharmacy.PharmacistLastName,
    pharmacy.PharmacistSuffix,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Pull the pharmacy NPI out of the loosely-typed ``Identifications``
 * bag. DoseSpot's RTPB swagger types this as an open object so payers
 * can attach arbitrary id types (NPI, NABP, federal tax id, etc.); we
 * accept any key that case-insensitively matches an NPI-style label.
 */
function extractNpi(pharmacy: PrescriptionBenefitsPharmacyT): string | null {
  const ids = pharmacy.Identifications;
  if (!ids || typeof ids !== 'object') return null;
  for (const [key, value] of Object.entries(ids)) {
    const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
    if (normalized === 'npi' || normalized === 'npinumber' || normalized === 'npiid') {
      if (value == null) return null;
      const str = String(value).trim();
      return str === '' ? null : str;
    }
  }
  return null;
}

/** ``Identifications`` keys that have first-class rows elsewhere in the
 * detail block; skip them when listing "other" identifications so we
 * don't render duplicates. */
const NPI_IDENTIFICATION_KEYS = new Set(['npi', 'npinumber', 'npiid']);

function listOtherIdentifications(
  pharmacy: PrescriptionBenefitsPharmacyT
): { label: string; value: string }[] {
  const ids = pharmacy.Identifications;
  if (!ids || typeof ids !== 'object') return [];
  const out: { label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(ids)) {
    const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
    if (NPI_IDENTIFICATION_KEYS.has(normalized)) continue;
    if (value == null) continue;
    const str = String(value).trim();
    if (str === '') continue;
    out.push({ label: key, value: str });
  }
  return out;
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) {
  if (value == null || value === '') return null;
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono' : undefined}>{value}</span>
    </>
  );
}

function PharmacyDetailBlock({ pharmacy }: { pharmacy: PrescriptionBenefitsPharmacyT }) {
  const [showMore, setShowMore] = useState(false);

  const address = formatPharmacyAddress(pharmacy);
  const pharmacistName = formatPharmacistName(pharmacy);
  const npi = extractNpi(pharmacy);
  const otherIdentifications = listOtherIdentifications(pharmacy);

  // Essential-row visibility. The block hides itself only when nothing
  // identifying is published — falling back through Identifications-based
  // ids covers RTPB rows where DoseSpot omits the structured pharmacy
  // slice entirely.
  const hasEssential =
    pharmacy.StoreName ||
    address ||
    pharmacy.PrimaryPhone ||
    pharmacy.PharmacyType ||
    npi ||
    pharmacy.NCPDPID;

  const additionalPhones: { label: string; value: string }[] = [];
  if (pharmacy.PhoneAdditional1) {
    additionalPhones.push({
      label: pharmacy.PhoneAdditionalType1 ?? 'Phone (alt 1)',
      value: pharmacy.PhoneAdditional1,
    });
  }
  if (pharmacy.PhoneAdditional2) {
    additionalPhones.push({
      label: pharmacy.PhoneAdditionalType2 ?? 'Phone (alt 2)',
      value: pharmacy.PhoneAdditional2,
    });
  }
  if (pharmacy.PhoneAdditional3) {
    additionalPhones.push({
      label: pharmacy.PhoneAdditionalType3 ?? 'Phone (alt 3)',
      value: pharmacy.PhoneAdditional3,
    });
  }

  const hasMore =
    pharmacy.PrimaryFax != null ||
    additionalPhones.length > 0 ||
    pharmacistName != null ||
    pharmacy.Email != null ||
    pharmacy.PharmacyId != null ||
    pharmacy.NCPDPID != null ||
    pharmacy.ServiceLevel != null ||
    (pharmacy.PharmacySpecialties?.length ?? 0) > 0 ||
    pharmacy.DistanceFromRequestedPharmacy != null ||
    pharmacy.Latitude != null ||
    pharmacy.Longitude != null ||
    otherIdentifications.length > 0;

  if (!hasEssential && !hasMore) return null;

  const coords =
    pharmacy.Latitude != null && pharmacy.Longitude != null
      ? `${pharmacy.Latitude}, ${pharmacy.Longitude}`
      : null;
  const distance =
    pharmacy.DistanceFromRequestedPharmacy != null
      ? `${pharmacy.DistanceFromRequestedPharmacy} mi`
      : null;
  const specialties = pharmacy.PharmacySpecialties?.join(', ') || null;

  return (
    <div className="mb-2 rounded border border-border/30 bg-muted/20 px-2.5 py-1.5 text-[11px]">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        {pharmacy.StoreName ? (
          <>
            <span className="text-muted-foreground">Store</span>
            <span className="font-medium">{pharmacy.StoreName}</span>
          </>
        ) : null}
        <DetailRow label="Type" value={pharmacy.PharmacyType ?? null} />
        <DetailRow label="Address" value={address} />
        <DetailRow label="Phone" value={pharmacy.PrimaryPhone ?? null} mono />
        <DetailRow label="NPI" value={npi} mono />
      </div>

      {showMore ? (
        <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-border/30 pt-1">
          <DetailRow label="Fax" value={pharmacy.PrimaryFax ?? null} mono />
          {additionalPhones.map((entry, idx) => (
            <DetailRow key={`addl-phone-${idx}`} label={entry.label} value={entry.value} mono />
          ))}
          <DetailRow label="Pharmacist" value={pharmacistName} />
          <DetailRow label="Email" value={pharmacy.Email ?? null} />
          <DetailRow label="Pharmacy ID" value={pharmacy.PharmacyId ?? null} mono />
          <DetailRow label="NCPDP ID" value={pharmacy.NCPDPID ?? null} mono />
          <DetailRow label="Service Level" value={pharmacy.ServiceLevel ?? null} />
          <DetailRow label="Specialties" value={specialties} />
          <DetailRow label="Distance" value={distance} />
          <DetailRow label="Coordinates" value={coords} mono />
          {otherIdentifications.map((entry) => (
            <DetailRow key={`id-${entry.label}`} label={entry.label} value={entry.value} mono />
          ))}
        </div>
      ) : null}

      {hasMore ? (
        <button
          type="button"
          className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={showMore}
        >
          {showMore ? 'Show less' : 'Show more'}
          <ChevronDown
            className={cn('size-3 transition-transform', showMore && 'rotate-180')}
            aria-hidden
          />
        </button>
      ) : null}
    </div>
  );
}

function PricingCoverageCard({
  coverage,
  label,
  defaultExpanded = false,
}: {
  coverage: PatientSpecificPricingCoverageT;
  label: string;
  defaultExpanded?: boolean;
}) {
  const [showFactors, setShowFactors] = useState(defaultExpanded);
  const pricing = coverage.PricingCoverage;
  if (!pricing) return null;

  const pharmacy = coverage.Pharmacy;
  const alerts = pricing.CoverageAlerts ?? [];
  const hasAlerts = alerts.length > 0;

  return (
    <div className="rounded-md border border-border/50 bg-background/40 p-2.5 dark:border-border/30 dark:bg-background/20">
      <div className="mb-2 flex items-center justify-between text-[11px]">
        <span className="font-medium">{label}</span>
        {pharmacy?.NCPDPID ? (
          <span className="font-mono text-[10px] text-muted-foreground">
            NCPDP {pharmacy.NCPDPID}
          </span>
        ) : null}
      </div>

      {pharmacy ? <PharmacyDetailBlock pharmacy={pharmacy} /> : null}

      <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
        <PricingField
          label="Est. Patient Pay"
          value={pricing.EstimatedPatientPayAmount}
          prefix="$"
        />
        <PricingField label="Qty Priced" value={formatQuantityPriced(pricing)} />
        <PricingField label="Days Supply" value={pricing.DaysSupplyPriced} />
      </div>

      <div className="mt-2">
        <button
          type="button"
          className="flex w-full items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setShowFactors((v) => !v)}
        >
          Coverage factors
          {pricing.PriorAuthRequired ? (
            <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              PA
            </span>
          ) : null}
          {hasAlerts ? (
            <span className="ml-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
              Alerts
            </span>
          ) : null}
          <ChevronDown
            className={cn('ml-auto size-3 transition-transform', showFactors && 'rotate-180')}
            aria-hidden
          />
        </button>
        {showFactors ? <CoverageFactorsDetail pricing={pricing} /> : null}
      </div>
    </div>
  );
}

function CoverageFactorsDetail({ pricing }: { pricing: PricingCoverageFactorsT }) {
  const alertList = pricing.CoverageAlerts ?? [];

  return (
    <div className="mt-1.5 space-y-1.5 border-t border-border/30 pt-1.5">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 @md:grid-cols-3">
        <PricingField label="Drug Status" value={pricing.DrugStatus ?? pricing.DrugStatusCode} />
        <PricingField label="Formulary Status" value={pricing.FormularyStatus} />
        <PricingField
          label="Prior Auth"
          value={
            pricing.PriorAuthRequired
              ? 'Required'
              : pricing.PriorAuthRequired === false
                ? 'No'
                : null
          }
        />
        <PricingField label="Plan Pay" value={pricing.PlanPayAmount} prefix="$" />
        <PricingField label="OOP Applied" value={pricing.OOPAppliedAmount} prefix="$" />
        <PricingField label="OOP Remaining" value={pricing.OOPRemainingAmount} prefix="$" />
        <PricingField
          label="Deductible Applied"
          value={pricing.DeductibleAppliedAmount}
          prefix="$"
        />
        <PricingField
          label="Deductible Remaining"
          value={pricing.DeductibleRemainingAmount}
          prefix="$"
        />
      </div>
      {alertList.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Coverage Alerts
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {alertList.map((alert, idx) => (
              <li
                key={`alert-${idx}`}
                className="rounded border border-border/30 bg-muted/20 px-2 py-0.5 text-xs"
              >
                {alert.ReferenceCode ? (
                  <span className="font-medium">{alert.ReferenceCode} </span>
                ) : null}
                <LinkifyText text={alert.ReferenceText ?? ''} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

interface RtpbAlternativeRowModel {
  key: string;
  displayName: string;
  ndc: string | null;
  dispensableDrugId: number | null;
  coverages: PatientSpecificPricingCoverageT[];
  coverage: PrescriptionBenefitPayerAlternativeDetailT['Coverage'] | null | undefined;
}

function normalizeRtpbAlternatives(alternatives: PayerAlternativeT[]): RtpbAlternativeRowModel[] {
  return alternatives.flatMap((alt, altIdx) => {
    const details = alt.Details ?? [];
    if (details.length > 0) {
      return details.map((detail, detailIdx) => ({
        key: detail.Ndc ?? alt.Ndc ?? `rtpb-alt-${altIdx}-detail-${detailIdx}`,
        displayName: detail.DisplayName ?? alt.DisplayName ?? detail.Ndc ?? alt.Ndc ?? '—',
        ndc: detail.Ndc ?? alt.Ndc ?? null,
        dispensableDrugId: detail.DispensableDrugId ?? alt.DispensableDrugId ?? null,
        coverages:
          detail.PatientSpecificPricingCoverages ?? alt.PatientSpecificPricingCoverages ?? [],
        coverage: detail.Coverage ?? null,
      }));
    }

    return [
      {
        key: alt.Ndc ?? `rtpb-alt-${altIdx}`,
        displayName: alt.DisplayName ?? alt.Ndc ?? '—',
        ndc: alt.Ndc ?? null,
        dispensableDrugId: alt.DispensableDrugId ?? null,
        coverages: alt.PatientSpecificPricingCoverages ?? [],
        coverage: null,
      },
    ];
  });
}

function RtpbAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: PayerAlternativeT[];
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  const rows = normalizeRtpbAlternatives(alternatives);
  if (rows.length === 0) return null;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Pill className="size-3" aria-hidden />
        RTPB Alternatives
      </div>
      <div className="space-y-2">
        {rows.map((alt) => (
          <RtpbAlternativeCard
            key={alt.key}
            alt={alt}
            onSelectAlternative={onSelectAlternative}
            isSelectingAlternative={isSelectingAlternative}
          />
        ))}
      </div>
    </div>
  );
}

function RtpbAlternativeCard({
  alt,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alt: RtpbAlternativeRowModel;
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const coverages = alt.coverages;
  const { primary, others } = partitionCoveragesByPharmacyType(coverages);
  const isClickable =
    onSelectAlternative !== undefined && !!alt.dispensableDrugId && !isSelectingAlternative;

  return (
    <div className="rounded-md border border-border/40 bg-background/40 dark:border-border/30 dark:bg-background/20">
      <div className="px-2.5 py-2">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{alt.displayName}</div>
            {alt.ndc ? (
              <span className="font-mono text-[10px] text-muted-foreground">NDC {alt.ndc}</span>
            ) : null}
          </div>
          {isClickable && onSelectAlternative ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              radius="pill"
              onClick={() => onSelectAlternative(alt.dispensableDrugId!)}
              aria-label={`Switch to ${alt.displayName}`}
            >
              <span className="text-[11px]">Use this</span>
              <ArrowRight className="ml-1 size-3" aria-hidden />
            </Button>
          ) : null}
        </div>

        {alt.coverage?.FormularyStatus ? (
          <div className="mt-1 text-[10px] text-muted-foreground">
            Coverage status:{' '}
            {formularyStatusLabel(alt.coverage.FormularyStatus) ?? alt.coverage.FormularyStatus}
          </div>
        ) : null}

        {primary?.Pharmacy ? <PharmacyDetailBlock pharmacy={primary.Pharmacy} /> : null}

        {primary?.PricingCoverage ? (
          <div className="mt-1.5 grid grid-cols-3 gap-x-4 gap-y-1">
            <PricingField
              label="Est. Patient Pay"
              value={primary.PricingCoverage.EstimatedPatientPayAmount}
              prefix="$"
            />
            <PricingField
              label="Qty Priced"
              value={formatQuantityPriced(primary.PricingCoverage)}
            />
            <PricingField label="Days Supply" value={primary.PricingCoverage.DaysSupplyPriced} />
          </div>
        ) : null}

        {others.length > 0 || primary?.PricingCoverage ? (
          <button
            type="button"
            className="mt-1.5 flex w-full items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
          >
            {primary?.PricingCoverage?.PriorAuthRequired ? (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                PA
              </span>
            ) : null}
            {primary?.PricingCoverage?.FormularyStatus != null ? (
              <span className="text-[10px]">Status {primary.PricingCoverage.FormularyStatus}</span>
            ) : null}
            {others.length > 0 ? (
              <span className="text-[10px]">
                + {others.length} other {others.length === 1 ? 'pharmacy' : 'pharmacies'}
              </span>
            ) : null}
            {coverages.some((c) => c.PricingCoverage?.CoverageAlerts?.length) ? (
              <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                Alerts
              </span>
            ) : null}
            <span className="ml-auto text-[10px]">Details</span>
            <ChevronDown
              className={cn('size-3 transition-transform', expanded && 'rotate-180')}
              aria-hidden
            />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-2 border-t border-border/30 px-2.5 py-2">
          {primary?.PricingCoverage ? (
            <CoverageFactorsDetail pricing={primary.PricingCoverage} />
          ) : null}
          {others.map((cov, idx) => (
            <PricingCoverageCard
              key={`alt-other-${idx}`}
              coverage={cov}
              label={pharmacyLabel(cov)}
              defaultExpanded={false}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function formatQuantityPriced(pricing: PricingCoverageFactorsT): string | null {
  const value = pricing.QuantityPricedValue;
  if (value == null) return null;
  const uom = pricing.QuantityPricedUnitOfMeasureType;
  return uom ? `${value} ${uom}` : String(value);
}

function PricingField({
  label,
  value,
  prefix,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
  prefix?: string;
}) {
  if (value == null || value === '') return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-medium">
        {prefix ?? ''}
        {String(value)}
      </div>
    </div>
  );
}

export function PayerAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: PayerAlternativeT[];
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Pill className="size-3" aria-hidden />
        Payer Alternatives
      </div>
      <div className="flex flex-col gap-1">
        {alternatives.map((alt, idx) => {
          const dispensableDrugId = alt.DispensableDrugId ?? null;
          const rawCode = alt.RepresentativeFormularyCoverage?.FormularyStatus ?? null;
          const bucket = bucketFormularyStatus(rawCode, null);
          const status =
            formularyStatusLabel(rawCode) ??
            alt.RepresentativeFormularyCoverage?.FormularyStatusText ??
            rawCode;
          const showCopay = bucket !== 'unknown' && bucket !== 'not-reimbursable';
          const copay = showCopay
            ? pickAlternativeRetailCopay(alt.RepresentativeFormularyCoverage?.CopayFactors)
            : null;
          const isClickable =
            onSelectAlternative !== undefined &&
            dispensableDrugId !== null &&
            !isSelectingAlternative;
          return (
            <AlternativeRow
              key={alt.Ndc ?? `payer-alt-${idx}`}
              displayName={alt.DisplayName ?? alt.Ndc ?? '—'}
              brand={alt.Brand}
              status={status}
              copay={copay}
              isClickable={isClickable}
              onSelect={
                onSelectAlternative && dispensableDrugId !== null
                  ? () => onSelectAlternative(dispensableDrugId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export function TherapeuticAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: TherapeuticAlternativesT[];
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Pill className="size-3" aria-hidden />
        Therapeutic Alternatives
      </div>
      <div className="flex flex-col gap-1">
        {alternatives.map((alt, idx) => {
          const dispensableDrugId = alt.DispensableDrugId ?? null;
          const rawCode = alt.FormularyStatus ?? null;
          const bucket = bucketFormularyStatus(rawCode, null);
          const status =
            formularyStatusLabel(rawCode) ?? alt.FormularyInfo?.FormularyStatusMessage ?? rawCode;
          const showCopay = bucket !== 'unknown' && bucket !== 'not-reimbursable';
          const copay = showCopay ? (alt.Copay ?? null) : null;
          const isClickable =
            onSelectAlternative !== undefined &&
            dispensableDrugId !== null &&
            !isSelectingAlternative;
          return (
            <AlternativeRow
              key={alt.Ndc ?? `therapeutic-alt-${idx}`}
              displayName={alt.FullDisplayString ?? alt.DisplayName ?? alt.Ndc ?? '—'}
              brand={alt.FormularyInfo?.IsGeneric != null ? !alt.FormularyInfo.IsGeneric : null}
              status={status}
              copay={copay}
              isClickable={isClickable}
              onSelect={
                onSelectAlternative && dispensableDrugId !== null
                  ? () => onSelectAlternative(dispensableDrugId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function AlternativeRow({
  displayName,
  brand,
  status,
  copay,
  isClickable,
  onSelect,
}: {
  displayName: string;
  brand: boolean | null | undefined;
  status: string | null;
  copay: PayerCopayT | null;
  isClickable: boolean;
  onSelect: (() => void) | undefined;
}) {
  const copayDescription = copay ? describeCopay(copay) : null;
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-md border border-border/40 bg-background/40 px-2.5 py-1.5',
        'dark:border-border/30 dark:bg-background/20'
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="truncate text-xs font-medium">{displayName}</div>
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
          {brand != null ? <span>{brand ? 'Brand' : 'Generic'}</span> : null}
          {status ? <span>· {status}</span> : null}
          {copayDescription ? <span>· {copayDescription}</span> : null}
        </div>
      </div>
      {isClickable && onSelect ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          radius="pill"
          onClick={onSelect}
          aria-label={`Switch to ${displayName}`}
        >
          <span className="text-[11px]">Use this</span>
          <ArrowRight className="ml-1 size-3" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PM.122.1: Linkify URLs in coverage alert text                     */
/* ------------------------------------------------------------------ */

const URL_PATTERN = /(https?:\/\/[^\s)]+)/i;

function LinkifyText({ text }: { text: string }) {
  if (!text) return null;
  const parts = text.split(URL_PATTERN);
  if (parts.length === 1) return <>{text}</>;

  return (
    <>
      {parts.map((part, idx) =>
        URL_PATTERN.test(part) ? (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noreferrer noopener"
            className="text-sky-700 underline underline-offset-2 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-100"
          >
            {part}
          </a>
        ) : (
          <span key={idx}>{part}</span>
        )
      )}
    </>
  );
}
